import { Flex, Icon, Text } from '@oplog/express';
import React, { FC, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { AddressTypeOutputDTO } from '../../../services/swagger';
import useCommonStore from '../../../store/global/commonStore';

interface IStationBox {
  station: AddressTypeOutputDTO;
}

export enum DiscriminatorTypes {
  PackingAddress = 'PackingAddress',
  WallPackingAddress = 'WallPackingAddress',
  HOVPackingAddress = 'HOVPackingAddress',
  SLAMAddress = 'SLAMAddress',
  ReturnAddress = 'ReturnAddress',
  ProblemSolverAddress = 'ProblemSolverAddress',
  MissingItemTransferAddress = 'MissingItemTransferAddress',
  SingleItemPackingAddress = 'SingleItemPackingAddress',
  SingleItemFastPackPackingAddress = 'SingleItemFastPackPackingAddress',
  ReceivingAddress = 'ReceivingAddress',
  RebinAddress = 'RebinAddress',
  HOVRebinAddress = 'HOVRebinAddress',
  SimplePackingAddress = 'SimplePackingAddress',
  RasStowStation = 'RasStowStation',
  RasPickStation = 'RasPickStation',
  RasOrderBasedPickStation = 'RasOrderBasedPickStation',
  RasProblemSolverStation = 'RasProblemSolverStation',
  ApprovedSLAMAddress = 'ApprovedSLAMAddress',
  WaybillStationAddress = 'WaybillStationAddress',
}

const StationBox: FC<IStationBox> = ({ station }): ReactElement => {
  const { t } = useTranslation();
  const [{ userInfo }] = useCommonStore();
  const stationIconMap = () => {
    switch (station.discriminator) {
      case DiscriminatorTypes.PackingAddress:
        return 'fal fa-boxes';
      case DiscriminatorTypes.WallPackingAddress:
        return 'fal fa-th-large';
      case DiscriminatorTypes.HOVPackingAddress:
        return 'fal fa-pallet-alt';
      case DiscriminatorTypes.SingleItemPackingAddress:
        return 'fal fa-cube';
      case DiscriminatorTypes.SingleItemFastPackPackingAddress:
        return 'fal fa-bolt';
      case DiscriminatorTypes.SimplePackingAddress:
        return 'fal fa-box';
      case DiscriminatorTypes.RebinAddress:
        return 'fal fa-hand-holding-box';
      case DiscriminatorTypes.HOVRebinAddress:
        return 'fal fa-hand-holding-box';
      case DiscriminatorTypes.ReturnAddress:
        return 'fal fa-undo';
      case DiscriminatorTypes.SLAMAddress:
        return 'fal fa-box-ballot';
      case DiscriminatorTypes.ApprovedSLAMAddress:
        return 'fal fa-box-ballot';
      case DiscriminatorTypes.ProblemSolverAddress:
        return 'fal fa-key';
      case DiscriminatorTypes.MissingItemTransferAddress:
        return 'fal fa-key';
      case DiscriminatorTypes.WaybillStationAddress:
        return 'fal fa-file-invoice';
      default:
        return '';
    }
  };

  const stationTypeLabelKey = (): string | undefined => {
    switch (station.discriminator) {
      case DiscriminatorTypes.PackingAddress:
        return 'TouchScreen.StationType.Multi';
      case DiscriminatorTypes.WallPackingAddress:
        return 'TouchScreen.StationType.Wall';
      case DiscriminatorTypes.HOVPackingAddress:
        return 'TouchScreen.StationType.HOV';
      case DiscriminatorTypes.SingleItemPackingAddress:
        return 'TouchScreen.StationType.Single';
      case DiscriminatorTypes.SingleItemFastPackPackingAddress:
        return 'TouchScreen.StationType.Fast';
      case DiscriminatorTypes.SimplePackingAddress:
        return 'TouchScreen.StationType.Simple';
      default:
        return undefined;
    }
  };

  const typeLabelKey = stationTypeLabelKey();

  return (
    <>
      <Flex
        borderRadius="8px"
        bg={
          [DiscriminatorTypes.SLAMAddress, DiscriminatorTypes.ApprovedSLAMAddress].includes(
            station.discriminator as DiscriminatorTypes
          )
            ? 'palette.softGrey'
            : 'palette.white'
        }
        minHeight={36}
        py={6}
        px={12}
        mb={6}
        alignItems="center"
        justifyContent="center"
        color="palette.slate_dark"
        fontWeight={500}
        fontSize={14}
      >
        <Icon name={stationIconMap()} letterSpacing="negativeLarge" />
        {typeLabelKey ? (
          <Text ml={8}>
            {t(typeLabelKey)}:{' '}
            <Text as="span" fontWeight={700}>
              {station.label}
            </Text>
          </Text>
        ) : (
          <Text ml={8}>{station.label}</Text>
        )}
      </Flex>
      <Flex
        borderRadius="8px"
        bg={
          [DiscriminatorTypes.SLAMAddress, DiscriminatorTypes.ApprovedSLAMAddress].includes(
            station.discriminator as DiscriminatorTypes
          )
            ? 'palette.softGrey'
            : 'palette.white'
        }
        minHeight={36}
        py={6}
        px={12}
        mb={16}
        alignItems="center"
        justifyContent="center"
        color="palette.slate_dark"
        fontWeight="500"
      >
        <Text fontSize="16">
          {userInfo?.givenName
            ? `${userInfo?.givenName} ${userInfo?.familyName}`
            : userInfo?.email?.split('@')[0]}
        </Text>
      </Flex>
    </>
  );
};
export default StationBox;
